import { useState } from "react";
import { Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { usePwaInstall } from "@/hooks/usePwaInstall";

/**
 * Permanent "Install App" button in the header. Shows whenever the app is
 * installable (Android Chrome/Edge) or on iOS Safari. Hidden when already in
 * standalone (installed) mode.
 */
export default function HeaderInstallButton({ variant = "desktop" }) {
  const { canInstall, isIos, isStandalone, install } = usePwaInstall();
  const [showIosHint, setShowIosHint] = useState(false);

  if (isStandalone) return null;
  if (!canInstall && !isIos) return null;

  const handleClick = async () => {
    if (canInstall) {
      await install();
    } else if (isIos) {
      setShowIosHint(true);
    }
  };

  const isMobile = variant === "mobile";

  return (
    <>
      <button
        onClick={handleClick}
        data-testid={`header-install-btn${isMobile ? "-mobile" : ""}`}
        className={`inline-flex items-center gap-1.5 rounded-full font-semibold transition-all
          ${isMobile
            ? "px-2.5 py-1.5 text-[11px] bg-gold-400 hover:bg-gold-500 text-amethyst-900"
            : "px-3.5 py-2 text-xs bg-gold-400 hover:bg-gold-500 text-amethyst-900 shadow-sm"}
        `}
        aria-label="Install Shakti Wings app"
      >
        <Download className="w-3.5 h-3.5" strokeWidth={2.4} />
        <span className="whitespace-nowrap">{isMobile ? "Install" : "Install App"}</span>
      </button>

      <Dialog open={showIosHint} onOpenChange={setShowIosHint}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Install Shakti Wings on iPhone</DialogTitle>
            <DialogDescription>Follow these steps in Safari:</DialogDescription>
          </DialogHeader>
          <ol className="text-sm space-y-2 text-gray-700">
            <li>1. Tap the <span className="font-mono px-1.5 py-0.5 bg-gray-100 rounded">Share ↑</span> icon at the bottom of Safari.</li>
            <li>2. Scroll down and tap <span className="font-semibold">Add to Home Screen</span>.</li>
            <li>3. Tap <span className="font-semibold">Add</span> in the top-right.</li>
            <li>4. Open Shakti Wings from your home screen like any other app.</li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}
